import {
  afterNextRender,
  Component,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

import { environment } from '../../../environments/environment';
import {
  BoundingBox,
  ImagenPublica,
  PinMapa,
  precioPrincipal,
} from '../../core/models/propiedad.model';
import { token } from '../../core/tokens';
import { GoogleMapsLoader } from '../../core/services/google-maps-loader.service';
import { PropiedadesPublicoService } from '../../core/services/propiedades-publico.service';
import { PropiedadCard } from '../../shared/propiedad-card/propiedad-card';
import {
  buscarObjetivo,
  esElObjetivo,
  NivelContorno,
  objetivoDelContorno,
} from './zona-partido';

/** Posición de cámara del mapa, persistible en la URL (DP07). */
export interface CamaraMapa {
  lat: number;
  lng: number;
  zoom: number;
}

/**
 * Separación entre el borde de abajo de la tarjeta y la coordenada del pin.
 * Es el `padding-bottom` de `.popup-anchor`, que despega la tarjeta del chip
 * de precio; acá se necesita el número para saber dónde queda su centro.
 */
const ANCLA_POPUP_PX = 46;

/** Cuadros que se espera a que la tarjeta esté maquetada antes de rendirse. */
const INTENTOS_MEDIDA = 5;

/**
 * Margen al encuadrar un contorno de zona: que el borde magenta no quede
 * pegado al borde del mapa ni debajo del botón de pantalla completa.
 */
const MARGEN_CONTORNO_PX = 48;

/**
 * Desplazamiento vertical, en píxeles de pantalla, entre la coordenada del pin
 * y el centro de la tarjeta que le cuelga encima.
 *
 * La tarjeta crece hacia arriba desde el ancla, así que su centro queda por
 * **encima** del pin: de ahí el signo. Para dejarla centrada en el mapa hay
 * que mover el centro del mapa esa misma cantidad, pero hacia abajo.
 */
export function offsetCentroTarjeta(altoTarjeta: number): number {
  return -(altoTarjeta / 2 + ANCLA_POPUP_PX);
}

/**
 * Vista de mapa del buscador (documento de estimación, módulo Mapa):
 *
 * - FE-02: mapa interactivo con auto-centrado/auto-zoom (fit bounds).
 * - FE-03: pines custom con el precio en su moneda original (estilo de marca,
 *   inspirado en el mapa de Zonaprop).
 * - FE-04 + DP02/DP03: contorno de zona vía Data-Driven Styling/Boundaries.
 *   Multi-zona: el contorno SOLO se dibuja cuando hay exactamente una zona
 *   seleccionada (definición del cliente); con varias zonas se encuadran
 *   todas sin contornos. El contorno es el del partido / departamento, el de
 *   CABA o el de una provincia, según las reglas de `zona-partido.ts`, y el
 *   mapa encuadra el contorno entero. Se apaga con
 *   `environment.contornoDeZonaHabilitado`.
 * - FE-05: popup anclado al pin al hacer clic (Figma node 245-1084), con
 *   carrusel, precio y acceso a la ficha.
 * - BE-06: al pan/zoom del usuario emite el bounding box del viewport para
 *   repedir pines; si la respuesta viene truncada se muestra un aviso.
 * - DP09: clustering de pins con `@googlemaps/markerclusterer` (cluster con
 *   contador en estilo de marca; clic = zoom al cluster).
 * - DP07: expone cámara y pin seleccionado (inputs iniciales + outputs) para
 *   que la página los persista en la URL y el retorno desde la ficha
 *   restaure el mapa exactamente como estaba.
 */
@Component({
  selector: 'app-mapa-propiedades',
  imports: [PropiedadCard],
  template: `
    <div class="mapa-wrap">
      <div #mapEl class="mapa"></div>

      @if (sinApiKey) {
        <div class="mapa-aviso">
          <p class="mapa-aviso__titulo">El mapa no está configurado</p>
          <p>
            Falta la API key de Google Maps. Configurá
            <code>googleMapsApiKey</code> en
            <code>src/environments/environment.ts</code> (ver documento de
            estimación, §7 — APIs de Google necesarias).
          </p>
        </div>
      } @else if (errorCarga()) {
        <div class="mapa-aviso">
          <p class="mapa-aviso__titulo">No pudimos cargar el mapa</p>
          <p>Revisá tu conexión e intentá de nuevo.</p>
        </div>
      }

      @if (cargando()) {
        <div class="mapa-cargando" role="status" aria-live="polite">
          <span class="spinner" aria-hidden="true"></span>
          Buscando propiedades…
        </div>
      } @else if (truncado()) {
        <div class="mapa-truncado" role="status">
          Estás viendo {{ pines().length }} de {{ total() }} propiedades.
          Acercá el mapa para ver el resto.
        </div>
      }

      <!--
        Popup anclado al pin: el nodo se reparenta a un AdvancedMarker.
        Es la misma tarjeta del listado sobre su panel flotante (FP-20).
        La galería del detalle llega después (FP-36): hasta entonces el input
        fotos va en null y la tarjeta muestra la portada sola.
      -->
      <div #popupEl class="popup-anchor">
        @if (pinSeleccionado(); as pin) {
          <app-propiedad-card
            [propiedad]="pin.tarjeta"
            [fotos]="galeriaPopup()"
            [popup]="true"
          />
        }
      </div>
    </div>
  `,
  styles: `
    .mapa-wrap {
      position: relative;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid var(--c-neutrals-grey_100);
    }

    .mapa {
      width: 100%;
      height: min(930px, calc(100vh - 280px));
      min-height: 480px;
      background: var(--c-neutrals-grey_50);
    }

    .mapa-aviso {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      text-align: center;
      padding: 24px;
      background: var(--c-neutrals-grey_50);
      color: var(--c-neutrals-grey_600);

      p {
        margin: 0;
        max-width: 480px;
      }
    }

    .mapa-aviso__titulo {
      font-size: 20px;
      font-weight: var(--fw-semibold);
      color: var(--c-neutrals-grey_800);
    }

    .mapa-cargando {
      position: absolute;
      top: 12px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      border-radius: 99999px;
      background: var(--c-neutrals-grey_0);
      color: var(--c-neutrals-grey_600);
      font-size: 13px;
      font-weight: var(--fw-semibold);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      white-space: nowrap;
    }

    .spinner {
      width: 14px;
      height: 14px;
      border: 2px solid var(--c-neutrals-grey_100);
      border-top-color: var(--c-phindi-rose_500);
      border-radius: 99999px;
      animation: girar 0.8s linear infinite;
    }

    @keyframes girar {
      to {
        transform: rotate(360deg);
      }
    }

    .mapa-truncado {
      position: absolute;
      top: 12px;
      left: 50%;
      transform: translateX(-50%);
      padding: 8px 16px;
      border-radius: 99999px;
      background: var(--c-neutrals-grey_800);
      color: var(--c-neutrals-grey_0);
      font-size: 13px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
      white-space: nowrap;
    }

    /* Ancla del popup: el AdvancedMarker posiciona su borde inferior sobre
       la coordenada del pin; el padding lo despega del chip de precio */
    .popup-anchor {
      padding-bottom: 46px;
    }
  `,
})
export class MapaPropiedades implements OnDestroy {
  /** Pines a renderizar (ya filtrados por la página). */
  readonly pines = input.required<PinMapa[]>();
  readonly total = input(0);
  readonly truncado = input(false);
  /**
   * Zonas buscadas (DP02/DP03). Con exactamente una zona se dibuja su
   * contorno; con varias solo se encuadran (sin contornos, definición del
   * cliente).
   */
  readonly localidades = input<string[]>([]);
  /** Estado de carga de la búsqueda (FE-07). */
  readonly cargando = input(false);
  /** Contador: al incrementarse fuerza el auto-encuadre (cambio de filtros). */
  readonly encuadre = input(0);
  /** DP07: cámara inicial restaurada desde la URL (anula el auto-encuadre). */
  readonly camaraInicial = input<CamaraMapa | undefined>(undefined);
  /** DP07: pin a reseleccionar al volver de la ficha. */
  readonly pinInicial = input<string | undefined>(undefined);

  /** Viewport movido por el usuario (BE-06): repedir pines con este bbox. */
  readonly boundsChange = output<BoundingBox>();
  /** DP07: cámara tras un movimiento del usuario, para persistir en la URL. */
  readonly camaraChange = output<CamaraMapa>();
  /** DP07: id del pin seleccionado (null al cerrar el popup). */
  readonly seleccionChange = output<string | null>();

  private readonly loader = inject(GoogleMapsLoader);
  private readonly service = inject(PropiedadesPublicoService);
  /** Para el `afterNextRender` de `centrarPopup`, que corre fuera del ctor. */
  private readonly injector = inject(Injector);
  private readonly mapEl =
    viewChild.required<ElementRef<HTMLDivElement>>('mapEl');
  private readonly popupEl =
    viewChild.required<ElementRef<HTMLDivElement>>('popupEl');

  readonly sinApiKey = !this.loader.disponible;
  readonly errorCarga = signal(false);
  readonly pinSeleccionado = signal<PinMapa | null>(null);

  /**
   * Galería del pin abierto (FP-36). Referencia estable —la exige el input
   * `fotos` de la tarjeta— y `null` mientras no haya nada que mostrar, que es
   * cuando la tarjeta cae a la portada sola.
   */
  readonly galeriaPopup = signal<ImagenPublica[] | null>(null);

  /**
   * Galerías ya pedidas, por id. Vive con el componente, así que se pierde al
   * pasar a la vista grilla o al entrar a la ficha (el `@if` de la página lo
   * destruye): el precio de eso es una request repetida al volver, y a cambio
   * el cliente HTTP no se queda con estado de una pantalla.
   */
  private readonly galeriasPorId = new Map<string, ImagenPublica[]>();

  /**
   * Ids con la request del detalle todavía en vuelo. La cache se llena recién
   * con la respuesta, así que sin esto volver a un pin abierto hace medio
   * segundo dispara una segunda request idéntica. No se cachea el error a
   * propósito: el id sale del set pase lo que pase, y un fallo de red se
   * reintenta al reabrir el pin.
   */
  private readonly galeriasEnVuelo = new Set<string>();

  private mapa?: google.maps.Map;
  private markerLib?: google.maps.MarkerLibrary;
  private marcadores = new Map<
    string,
    { marker: google.maps.marker.AdvancedMarkerElement; chip: HTMLElement }
  >();
  /** Agrupador de pins (DP09). */
  private clusterer?: MarkerClusterer;
  private idSeleccionado?: string;
  /** Marker que ancla el popup (FE-05) a la coordenada del pin. */
  private popupMarker?: google.maps.marker.AdvancedMarkerElement;
  private movimientoProgramatico = false;
  private idleTimer?: ReturnType<typeof setTimeout>;
  private geocoder?: google.maps.Geocoder;
  /** Turno de `resaltarZonas`: descarta respuestas de búsquedas viejas. */
  private busquedaZonas = 0;
  /** Capa con el contorno dibujado: la única que hay que borrar después. */
  private capaPintada?: google.maps.FeatureLayer;
  private ultimoEncuadre = 0;
  /** DP07: el pin inicial se reselecciona una sola vez. */
  private pinInicialPendiente?: string;

  constructor() {
    afterNextRender(() => void this.iniciar());

    effect(() => {
      const pines = this.pines();
      if (this.mapa) this.renderizarPines(pines);
    });

    effect(() => {
      const encuadre = this.encuadre();
      if (this.mapa && encuadre !== this.ultimoEncuadre) {
        this.ultimoEncuadre = encuadre;
        this.encuadrarPines();
      }
    });

    effect(() => {
      const localidades = this.localidades();
      if (this.mapa) void this.resaltarZonas(localidades);
    });
  }

  ngOnDestroy(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
  }

  // ── Inicialización ─────────────────────────────────────────────────────────

  private async iniciar(): Promise<void> {
    if (this.sinApiKey) return;
    try {
      await this.loader.load();
      const { Map: GMap } = (await google.maps.importLibrary(
        'maps',
      )) as google.maps.MapsLibrary;
      this.markerLib = (await google.maps.importLibrary(
        'marker',
      )) as google.maps.MarkerLibrary;

      // DP07: si hay cámara restaurada (vuelta desde la ficha) el mapa abre
      // exactamente donde estaba; si no, Buenos Aires hasta que haya pines.
      const camara = this.camaraInicial();
      this.mapa = new GMap(this.mapEl().nativeElement, {
        center: camara
          ? { lat: camara.lat, lng: camara.lng }
          : { lat: -34.6037, lng: -58.3816 },
        zoom: camara?.zoom ?? 12,
        mapId: environment.googleMapsMapId,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
      });

      // DP09: los marcadores se agrupan; clic en un cluster = zoom (default).
      this.clusterer = new MarkerClusterer({
        map: this.mapa,
        markers: [],
        renderer: { render: (cluster) => this.renderizarCluster(cluster) },
      });

      // El primer idle tras crear el mapa no es un movimiento del usuario.
      this.movimientoProgramatico = true;
      this.mapa.addListener('idle', () => this.onIdle());
      this.mapa.addListener('click', () => this.seleccionar(null));

      this.pinInicialPendiente = this.pinInicial();
      this.renderizarPines(this.pines());
      if (!camara) this.encuadrarPines();
      await this.resaltarZonas(this.localidades(), !camara);
    } catch (e) {
      console.error('Error inicializando Google Maps', e);
      this.errorCarga.set(true);
    }
  }

  // ── Pines con precio (FE-03) + clustering (DP09) ───────────────────────────

  private renderizarPines(pines: PinMapa[]): void {
    if (!this.mapa || !this.markerLib || !this.clusterer) return;
    const { AdvancedMarkerElement } = this.markerLib;

    const idsNuevos = new Set(pines.map((p) => p.id));

    // Quitar del cluster los marcadores que ya no están
    const eliminados: google.maps.marker.AdvancedMarkerElement[] = [];
    for (const [id, { marker }] of this.marcadores) {
      if (!idsNuevos.has(id)) {
        eliminados.push(marker);
        this.marcadores.delete(id);
      }
    }

    // Agregar los nuevos (los existentes se conservan: menos parpadeo al
    // repedir por viewport). Los markers NO se asignan al mapa: los maneja
    // el clusterer (DP09).
    const agregados: google.maps.marker.AdvancedMarkerElement[] = [];
    for (const pin of pines) {
      if (this.marcadores.has(pin.id)) continue;

      const chip = document.createElement('div');
      chip.className = 'pin-precio';
      chip.textContent = precioPrincipal(pin);

      const marker = new AdvancedMarkerElement({
        position: { lat: pin.latitud, lng: pin.longitud },
        content: chip,
        gmpClickable: true,
        title: `${pin.tarjeta.direccion}, ${pin.tarjeta.localidad}`,
      });
      marker.addListener('gmp-click', () => this.seleccionar(pin));

      this.marcadores.set(pin.id, { marker, chip });
      agregados.push(marker);
    }

    if (eliminados.length) this.clusterer.removeMarkers(eliminados, true);
    if (agregados.length) this.clusterer.addMarkers(agregados, true);
    if (eliminados.length || agregados.length) this.clusterer.render();

    // Si la propiedad seleccionada salió del resultado, cerrar la tarjeta
    if (this.idSeleccionado && !idsNuevos.has(this.idSeleccionado)) {
      this.seleccionar(null);
    }

    // DP07: reseleccionar el pin con el que se entró a la ficha (una vez)
    if (this.pinInicialPendiente) {
      const pin = pines.find((p) => p.id === this.pinInicialPendiente);
      if (pin) {
        this.pinInicialPendiente = undefined;
        this.seleccionar(pin);
      }
    }
  }

  /** Cluster con contador en estilo de marca (DP09). */
  private renderizarCluster(cluster: {
    count: number;
    position: google.maps.LatLng;
  }): google.maps.marker.AdvancedMarkerElement {
    const div = document.createElement('div');
    div.className = 'pin-cluster';
    div.textContent = String(cluster.count);
    return new this.markerLib!.AdvancedMarkerElement({
      position: cluster.position,
      content: div,
      // Los clusters más grandes por encima de los chicos (criterio de la
      // librería para sus íconos default)
      zIndex: 1000 + cluster.count,
    });
  }

  private seleccionar(pin: PinMapa | null): void {
    const cambio = (pin?.id ?? null) !== (this.idSeleccionado ?? null);
    if (this.idSeleccionado) {
      this.marcadores
        .get(this.idSeleccionado)
        ?.chip.classList.remove('pin-precio--seleccionado');
    }
    this.idSeleccionado = pin?.id;
    if (pin) {
      this.marcadores
        .get(pin.id)
        ?.chip.classList.add('pin-precio--seleccionado');
    }
    this.pinSeleccionado.set(pin);
    this.cargarGaleria(pin);
    this.anclarPopup(pin);
    if (pin) this.centrarPopup(pin);
    // DP07: la página persiste la selección en la URL
    if (cambio) this.seleccionChange.emit(pin?.id ?? null);
  }

  /**
   * Deja la tarjeta recién abierta en el centro del mapa.
   *
   * De la tarjeta se toma **sólo el alto**, nunca su posición en pantalla. La
   * primera versión medía el rectángulo del nodo del popup, y de ahí salían
   * los dos síntomas que se vieron en dev: el primer pin no se centraba
   * —cuando el `AdvancedMarker` todavía no había colocado el nodo, la medida
   * no servía— y a partir del segundo se centraba contra la posición del pin
   * anterior. Con el alto alcanza: dónde cae el pin se sabe por su coordenada,
   * que es dato firme.
   *
   * El destino se calcula proyectando el pin a píxeles de mundo y corriéndolo
   * hacia el sur la mitad de la tarjeta más el ancla; convertido de vuelta a
   * lat/lng, es el centro que deja la tarjeta justo en el medio.
   *
   * El movimiento **no** se marca como programático: recentrar cambia el
   * viewport lo suficiente como para que valga repedir los pines (BE-06) y
   * persistir la cámara (DP07), así que al volver de la ficha el mapa ya
   * aparece encuadrado.
   */
  private centrarPopup(pin: PinMapa, intento = 0): void {
    afterNextRender(
      () => {
        if (!this.mapa || this.idSeleccionado !== pin.id) return;

        const tarjeta = this.popupEl().nativeElement.firstElementChild;
        const alto = tarjeta?.getBoundingClientRect().height ?? 0;
        // Recién montado, el nodo puede no estar maquetado todavía. Se le dan
        // unos cuadros antes de dejarlo pasar: sin alto no hay centro posible.
        if (!alto) {
          if (intento < INTENTOS_MEDIDA) {
            requestAnimationFrame(() => this.centrarPopup(pin, intento + 1));
          }
          return;
        }

        const destino = this.centroParaTarjeta(pin, alto);
        if (destino) this.mapa.panTo(destino);
      },
      { injector: this.injector },
    );
  }

  /**
   * Centro del mapa que deja centrada una tarjeta de `alto` px colgada del
   * pin. `null` si el mapa todavía no expone su proyección, que es el caso al
   * restaurar desde la URL: ahí la cámara guardada ya viene encuadrada.
   */
  private centroParaTarjeta(
    pin: PinMapa,
    alto: number,
  ): google.maps.LatLng | null {
    const proyeccion = this.mapa?.getProjection();
    const zoom = this.mapa?.getZoom();
    if (!proyeccion || zoom == null) return null;

    const punto = proyeccion.fromLatLngToPoint(
      new google.maps.LatLng(pin.latitud, pin.longitud),
    );
    if (!punto) return null;

    // `fromLatLngToPoint` trabaja en el mundo de 256 px del zoom 0; cada nivel
    // de zoom duplica la escala.
    //
    // El centro buscado es, literalmente, dónde cae el centro de la tarjeta:
    // el pin corrido por `offsetCentroTarjeta`. Como la tarjeta cuelga hacia
    // arriba ese offset es negativo, y el eje y crece hacia el sur, así que el
    // centro termina al NORTE del pin. Sumar es lo correcto; restar lo dejaba
    // al sur y mandaba la tarjeta fuera del mapa por arriba.
    const escala = 2 ** zoom;
    return proyeccion.fromPointToLatLng(
      new google.maps.Point(
        punto.x,
        punto.y + offsetCentroTarjeta(alto) / escala,
      ),
    );
  }

  /**
   * Galería del popup, bajo demanda (FP-36).
   *
   * El contrato del mapa no la trae y no puede traerla: `GET /mapa` devuelve
   * 500 pines por defecto (hasta 2000) y se repite en cada pan/zoom, así que
   * sumarle 5 fotos × 7 URLs por pin lleva la respuesta de ~420 kB a ~2 MB
   * para alimentar un popup que se abre de a uno. Se pide el detalle —que ya
   * devuelve `galeria` con variantes— sólo al abrir el popup: un pin abierto
   * dos veces se sirve de la cache, y si la primera request sigue en vuelo se
   * espera esa en vez de duplicarla.
   */
  private cargarGaleria(pin: PinMapa | null): void {
    if (!pin) {
      this.galeriaPopup.set(null);
      return;
    }

    const { id } = pin;
    const cacheada = this.galeriasPorId.get(id);
    this.galeriaPopup.set(cacheada ?? null);
    if (cacheada || this.galeriasEnVuelo.has(id)) return;

    this.galeriasEnVuelo.add(id);
    this.service.detalle(id).subscribe({
      next: (detalle) => {
        // Mismo orden de preferencia que la ficha: `galeria` trae las
        // variantes; `imagenes` es el respaldo plano.
        const galeria: ImagenPublica[] = detalle.galeria?.length
          ? detalle.galeria
          : detalle.imagenes.map((url) => ({ url }));
        this.galeriasPorId.set(id, galeria);
        this.galeriasEnVuelo.delete(id);
        // El usuario pudo cambiar de pin —o cerrar el popup— mientras la
        // request estaba en vuelo.
        if (this.idSeleccionado === id) this.galeriaPopup.set(galeria);
      },
      // El popup ya está mostrando la portada: sin galería sigue completo,
      // apenas sin flechas.
      error: () => this.galeriasEnVuelo.delete(id),
    });
  }

  /** Ancla el popup a la coordenada del pin con un AdvancedMarker propio. */
  private anclarPopup(pin: PinMapa | null): void {
    if (!this.mapa || !this.markerLib) return;
    if (!pin) {
      if (this.popupMarker) this.popupMarker.map = null;
      return;
    }
    this.popupMarker ??= new this.markerLib.AdvancedMarkerElement({
      content: this.popupEl().nativeElement,
      zIndex: 10000,
    });
    this.popupMarker.position = { lat: pin.latitud, lng: pin.longitud };
    this.popupMarker.map = this.mapa;
  }

  // ── Auto-centrado / auto-zoom (FE-02) ──────────────────────────────────────

  private encuadrarPines(): void {
    if (!this.mapa) return;
    const pines = this.pines();
    if (!pines.length) return;
    const bounds = new google.maps.LatLngBounds();
    for (const pin of pines) {
      bounds.extend({ lat: pin.latitud, lng: pin.longitud });
    }
    this.movimientoProgramatico = true;
    this.mapa.fitBounds(bounds, 64);
  }

  // ── Repedido por viewport (BE-06) + cámara persistida (DP07) ──────────────

  private onIdle(): void {
    if (this.movimientoProgramatico) {
      this.movimientoProgramatico = false;
      return;
    }
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      const bounds = this.mapa?.getBounds();
      const centro = this.mapa?.getCenter();
      const zoom = this.mapa?.getZoom();
      if (!bounds) return;
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      this.boundsChange.emit({
        latMin: sw.lat(),
        latMax: ne.lat(),
        lngMin: sw.lng(),
        lngMax: ne.lng(),
      });
      if (centro && zoom != null) {
        this.camaraChange.emit({
          lat: centro.lat(),
          lng: centro.lng(),
          zoom,
        });
      }
    }, 400);
  }

  // ── Contorno de zona (FE-04 + DP02/DP03) ───────────────────────────────────

  /**
   * Multi-zona (DP02/DP03): con exactamente UNA zona se dibuja el contorno
   * de su partido, CABA o provincia, si corresponde (`contornoDeZona`), y se
   * encuadra;
   * con varias zonas se geocodifican todas y se encuadra el conjunto SIN
   * contornos (definición del cliente). `encuadrar=false` evita pisar la
   * cámara restaurada al volver de la ficha (DP07).
   */
  private async resaltarZonas(
    localidades: string[],
    encuadrar = true,
  ): Promise<void> {
    if (!this.mapa) return;

    // Una búsqueda nueva invalida la anterior: las geocodificaciones son
    // asíncronas y una respuesta vieja no debe pisar el contorno actual.
    const busqueda = ++this.busquedaZonas;

    // El contorno anterior se borra siempre; se redibuja sólo si corresponde.
    if (this.capaPintada) this.capaPintada.style = null;
    this.capaPintada = undefined;
    if (!localidades.length) return;

    try {
      this.geocoder ??= new google.maps.Geocoder();
      const resultados = await Promise.all(
        localidades.map((zona) =>
          this.geocoder!.geocode({ address: `${zona}, Argentina` })
            .then(({ results }) => results[0] ?? null)
            .catch((e) => {
              console.warn(`No se pudo geocodificar la zona "${zona}"`, e);
              return null;
            }),
        ),
      );
      if (busqueda !== this.busquedaZonas) return;
      const validos = resultados.filter((r) => r !== null);
      if (!validos.length) return;

      // Contorno SOLO con zona única y con el interruptor prendido. Se
      // resuelve antes de encuadrar: con contorno, el mapa encuadra el
      // contorno entero (el partido suele ser más grande que la localidad);
      // sin contorno, la zona como hasta ahora.
      const contorno =
        environment.contornoDeZonaHabilitado && localidades.length === 1
          ? await this.contornoDeZona(localidades[0], validos[0])
          : null;
      if (busqueda !== this.busquedaZonas) return;

      if (encuadrar) {
        const bounds = new google.maps.LatLngBounds();
        if (contorno) {
          const { geometry } = contorno.lugar;
          bounds.union(geometry.bounds ?? geometry.viewport);
        } else {
          for (const r of validos) bounds.union(r.geometry.viewport);
        }
        this.movimientoProgramatico = true;
        this.mapa.fitBounds(bounds, contorno ? MARGEN_CONTORNO_PX : undefined);
      }

      // Contorno magenta (Figma: stroke rose-500 + 15%)
      if (contorno) {
        const placeId = contorno.lugar.place_id;
        const magenta = token('c-phindi-rose_500', '#E61678');
        this.capaPintada = contorno.capa;
        contorno.capa.style = (
          params: google.maps.FeatureStyleFunctionOptions,
        ) =>
          (params.feature as google.maps.PlaceFeature).placeId === placeId
            ? {
                strokeColor: magenta,
                strokeOpacity: 1,
                strokeWeight: 3,
                fillColor: magenta,
                fillOpacity: 0.15,
              }
            : null;
      }
    } catch (e) {
      console.warn('No se pudieron geocodificar las zonas', e);
    }
  }

  /**
   * Capa de Google donde se pinta cada nivel: partidos / departamentos en el
   * nivel 2, provincias y CABA en el nivel 1 (Google no tiene polígonos de
   * localidades para la Argentina, ver `zona-partido.ts`). Se pide sólo
   * cuando se va a pintar: si el estilo del Map ID no tiene la capa (p. ej.
   * DEMO_MAP_ID, o un ambiente sin el nivel 1 configurado), Google deja un
   * error en consola en cada `getFeatureLayer`, y el estilo no tiene efecto.
   */
  private capaDe(nivel: NivelContorno): google.maps.FeatureLayer | undefined {
    try {
      return this.mapa?.getFeatureLayer(
        nivel === 'provincia'
          ? google.maps.FeatureType.ADMINISTRATIVE_AREA_LEVEL_1
          : google.maps.FeatureType.ADMINISTRATIVE_AREA_LEVEL_2,
      );
    } catch {
      // Sin data-driven styling disponible: se omite el contorno.
      return undefined;
    }
  }

  /**
   * Lugar a contornear y la capa donde se pinta, o `null` si a la zona no le
   * corresponde contorno (ver `objetivoDelContorno`). Si la geocodificación
   * ya devolvió ese lugar se usa directo; si devolvió algo adentro (una
   * localidad), se busca por geocodificación inversa de su centro (una
   * consulta más, sólo cuando corresponde dibujar).
   */
  private async contornoDeZona(
    zona: string,
    resultado: google.maps.GeocoderResult,
  ): Promise<{
    lugar: google.maps.GeocoderResult;
    capa: google.maps.FeatureLayer;
  } | null> {
    const objetivo = objetivoDelContorno(zona, resultado);
    const capa = objetivo ? this.capaDe(objetivo.nivel) : undefined;
    if (!objetivo || !capa) return null;
    if (esElObjetivo(resultado, objetivo)) return { lugar: resultado, capa };

    try {
      const { results } = await this.geocoder!.geocode({
        location: resultado.geometry.location,
      });
      const lugar = buscarObjetivo(results, objetivo);
      return lugar ? { lugar, capa } : null;
    } catch (e) {
      console.warn(`No se pudo obtener el contorno de "${zona}"`, e);
      return null;
    }
  }
}
